import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Tabs, Dropdown, ToggleGroup, Toast, Text } from "components";
import Analytics from "analytics";
import { EVENTS } from "analytics/events";
import { selectCurrentUser, selectCurrentUserId } from "features/auth/authSlice";
import {
  watchManagedGamesAction,
  stopWatchingManagedGamesAction,
  selectManagedGamesLoading,
  selectTotalManagedGames,
  selectGameCounts,
  selectHeroGame,
  selectFilteredSortedGames,
} from "../managedGamesSlice";
import useGameActions from "./useGameActions";
import DashboardHeader from "./components/DashboardHeader";
import DashboardSidebar from "./components/DashboardSidebar";
import DashboardGreeting from "./components/DashboardGreeting";
import HeroCard from "./components/HeroCard";
import GameGrid from "./components/GameGrid";
import QueueTable from "./components/QueueTable";
import PrecraftedStrip from "./components/PrecraftedStrip";
import { GridIcon, ListIcon } from "./components/icons";
import "./managerHome.css";

const VIEW_KEY = "gx-dashboard-view";

const SORT_OPTIONS = [
  { value: "touched", label: "Last touched" },
  { value: "edited", label: "Last edited" },
  { value: "created", label: "Last created" },
  { value: "name", label: "A–Z" },
];

const VIEW_OPTIONS = [
  { value: "grid", icon: <GridIcon />, ariaLabel: "Grid view" },
  { value: "list", icon: <ListIcon />, ariaLabel: "List view" },
];

const EMPTY_MESSAGES = {
  all: "No games yet — create your first one.",
  drafts: "No drafts right now.",
  ready: "Nothing ready to launch yet. Mark a finished draft ready to see it here.",
  "in-progress": "No games in progress.",
  played: "No played games yet. Launch a ready game to see it here.",
  archived: "Nothing archived.",
};

const firstNameOf = (user) =>
  user?.displayName ? user.displayName.trim().split(/\s+/)[0] : "there";

const ManagerHome = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const user = useSelector(selectCurrentUser);
  const currentUserId = useSelector(selectCurrentUserId);
  const loading = useSelector(selectManagedGamesLoading);
  const anyGames = useSelector(selectTotalManagedGames);
  const counts = useSelector(selectGameCounts);
  const heroGame = useSelector(selectHeroGame);

  const filter = searchParams.get("filter") || "all";
  const sort = searchParams.get("sort") || "touched";
  const view = searchParams.get("view") || localStorage.getItem(VIEW_KEY) || "grid";

  const games = useSelector((state) =>
    selectFilteredSortedGames(state, filter, sort)
  );

  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const notify = useCallback(
    (message, severity = "success") => setToast({ open: true, message, severity }),
    []
  );
  const buildActions = useGameActions(notify);

  useEffect(() => {
    Analytics.track(EVENTS.CREATOR_ONBOARDED);
  }, []);

  useEffect(() => {
    if (!currentUserId) return undefined;
    dispatch(watchManagedGamesAction(currentUserId));
    return () => dispatch(stopWatchingManagedGamesAction());
  }, [dispatch, currentUserId]);

  const setParam = useCallback(
    (key, value) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(key, value);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setView = useCallback(
    (v) => {
      localStorage.setItem(VIEW_KEY, v);
      setParam("view", v);
    },
    [setParam]
  );

  const filterTabs = [
    { value: "all", label: "All", count: counts.all },
    { value: "drafts", label: "Drafts", count: counts.drafts },
    { value: "ready", label: "Ready", count: counts.ready },
    { value: "in-progress", label: "In progress", count: counts.inProgress },
    { value: "played", label: "Played", count: counts.played },
  ];

  const showLibrary = anyGames > 0;
  const isArchiveView = filter === "archived";
  const emptyMessage = EMPTY_MESSAGES[filter] || EMPTY_MESSAGES.all;

  return (
    <div className="surface-manager da-root">
      <DashboardHeader />

      <div className="da-body">
        <DashboardSidebar
          counts={counts}
          activeFilter={filter}
          onFilter={(f) => setParam("filter", f)}
        />

        <main className="da-main">
          <DashboardGreeting firstName={firstNameOf(user)} counts={counts} />

          {loading && !anyGames ? (
            <div className="da-loading">
              <Text variant="body" tone="muted">
                Loading your games…
              </Text>
            </div>
          ) : (
            <>
              {!isArchiveView && (
                <HeroCard
                  game={heroGame}
                  actions={heroGame ? buildActions(heroGame) : null}
                  counts={counts}
                />
              )}

              {showLibrary && (
                <>
                  <div className="da-controls">
                    {isArchiveView ? (
                      <div />
                    ) : (
                      <Tabs
                        items={filterTabs}
                        value={filter}
                        onChange={(f) => setParam("filter", f)}
                      />
                    )}
                    <div className="da-controls-right">
                      <Dropdown
                        label="Sort:"
                        value={sort}
                        options={SORT_OPTIONS}
                        onChange={(s) => setParam("sort", s)}
                        size="sm"
                      />
                      <ToggleGroup
                        options={VIEW_OPTIONS}
                        value={view}
                        onChange={setView}
                        size="sm"
                      />
                    </div>
                  </div>

                  {view === "list" ? (
                    <QueueTable
                      games={games}
                      buildActions={buildActions}
                      emptyMessage={emptyMessage}
                    />
                  ) : (
                    <GameGrid
                      games={games}
                      buildActions={buildActions}
                      notify={notify}
                      emptyMessage={emptyMessage}
                      showNewTile={!isArchiveView}
                    />
                  )}

                  {!isArchiveView && <PrecraftedStrip />}
                </>
              )}
            </>
          )}
        </main>
      </div>

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </div>
  );
};

export { ManagerHome };
